import { IconAlertTriangle, IconInfoCircle } from "@tabler/icons-react";
import { useTranslations } from "next-intl";
import type { EmailPreflightWarning } from "@/types/AttachmentDrop";
import { isIntentionalDropReason } from "@/types/AttachmentDrop";

interface AttachmentPreflightWarningBlockProps {
  warnings: EmailPreflightWarning[] | null;
  loading: boolean;
}

/**
 * Pre-send attachment warning (send modal): lists the attachments the
 * preflight predicts will NOT reach the selected recipient — failures first
 * (error tone: wallet signing/icon, calendar start date, missing file), then
 * intentional targeting/config exclusions (info tone: condition mismatch,
 * portal-only). Warn-only: never wired into a send button's disabled state.
 * For TEST sends this block is the ONLY visibility (no audit row is written).
 * Fail-soft by contract: `warnings === null` (preflight error) renders nothing.
 */
export const AttachmentPreflightWarningBlock: React.FC<
  AttachmentPreflightWarningBlockProps
> = ({ warnings, loading }) => {
  const t = useTranslations("attachmentDropReason");
  // `t.has` fallback: an unknown future backend code renders the generic
  // label instead of the raw key (repo idiom, cf. GuestsDropDown tErrors).
  const reasonLabel = (code: string) => (t.has(code) ? t(code) : t("unknown"));

  if (loading) {
    return (
      <div className="surface-secondary border outline-quaternary rounded-md px-3 py-2 flex items-center gap-2">
        <IconInfoCircle size={14} className="font-tertiary shrink-0" />
        <span className="body-3-medium font-tertiary">
          {t("preflightLoading")}
        </span>
      </div>
    );
  }

  if (!warnings || warnings.length === 0) return null;

  const failures = warnings.filter((w) => !isIntentionalDropReason(w.code));
  const intentional = warnings.filter((w) => isIntentionalDropReason(w.code));

  const rowLabel = (w: EmailPreflightWarning) =>
    w.attachmentName || w.filename || w.docTemplateId || "";

  const renderRow = (w: EmailPreflightWarning, i: number, isError: boolean) => {
    const Icon = isError ? IconAlertTriangle : IconInfoCircle;
    const label = rowLabel(w);
    return (
      <div
        key={`${w.code}-${w.attachmentId ?? w.filename ?? i}`}
        className={`border rounded-md px-3 py-2 flex items-center gap-2 ${
          isError
            ? "surface-error outline-error"
            : "surface-secondary outline-quaternary"
        }`}
      >
        <Icon
          size={14}
          className={`shrink-0 ${isError ? "font-error" : "font-tertiary"}`}
        />
        {label && (
          <span
            className={`body-3-medium truncate ${
              isError ? "font-error" : "font-secondary"
            }`}
          >
            {label}
          </span>
        )}
        <span
          className={`body-3-regular flex-1 min-w-0 truncate ${
            isError ? "font-error" : "font-tertiary"
          }`}
        >
          {reasonLabel(w.code)}
        </span>
      </div>
    );
  };

  return (
    <div className="space-y-2">
      {/* Bold header so the block reads as the test-send safety net, not a
          footnote — failures mean files the recipient will NOT get. */}
      <div className="flex items-center gap-1.5">
        <IconAlertTriangle
          size={14}
          className={`shrink-0 ${failures.length > 0 ? "font-error" : "font-tertiary"}`}
        />
        <p
          className={`body-3-medium ${failures.length > 0 ? "font-error" : "font-secondary"}`}
        >
          {t("preflightTitle", { count: warnings.length })}
        </p>
      </div>
      {failures.map((w, i) => renderRow(w, i, true))}
      {intentional.map((w, i) => renderRow(w, i, false))}
    </div>
  );
};
